import React, { useRef } from 'react';

import Service from '../core/Service';
import ServiceProvider from '../core/ServiceProvider';

import { injectedService } from '@base/core';

const DIALOG_URL_ID_KEY = 'dialog';
const DIALOG_URL_PARAM_KEY = 'dialog-value';
const DEFERRED_DIALOG_KEY = 'deferredDialog';

function updateSearchParams(dialogId, param) {
  try {
    const searchParams = new URLSearchParams(window.location.search);
    if (dialogId) {
      if (searchParams.has(DIALOG_URL_ID_KEY)) {
        searchParams.set(DIALOG_URL_ID_KEY, dialogId);
      } else {
        searchParams.append(DIALOG_URL_ID_KEY, dialogId);
      }
      if (param) {
        if (searchParams.has(DIALOG_URL_PARAM_KEY)) {
          searchParams.set(DIALOG_URL_PARAM_KEY, param);
        } else {
          searchParams.append(DIALOG_URL_PARAM_KEY, param);
        }
      }
    } else {
      searchParams.delete(DIALOG_URL_ID_KEY);
      searchParams.delete(DIALOG_URL_PARAM_KEY);
    }
    const searchString = searchParams.toString();
    history.pushState({}, null, location.pathname + (searchString ? '?' + searchString : ''));
  } catch (e) {
    console.warn('updateSearchParams: history.pushState failed', e);
  }
}

export class DialogService extends Service {
  // dependencies
  api = null;
  dialogSettings = null;
  // refs
  dialogMapRef = null;
  // properties
  current = null;
  previous = null;
  param = null;
  history = [];
  options = {};

  initState(dependencies, refs) {
    super.initState(dependencies, refs);
    const searchParams = new URLSearchParams(window.location.search);
    const dialogId = searchParams.get(DIALOG_URL_ID_KEY);
    const dialogParam = searchParams.get(DIALOG_URL_PARAM_KEY);
    if (dialogId) {
      if (!this.dialogSettings[dialogId]) {
        console.error(`Attempt to open unexisted dialog '${dialogId}'`);
        this.close();
        return;
      }
      this.current = dialogId;
      this.param = dialogParam;
    } else {
      const deferred = localStorage.getItem(DEFERRED_DIALOG_KEY);
      if (deferred) {
        setTimeout(() => {
          this.open(deferred);
        });
      }
    }
  }

  open(dialogComponent, param, options = {}) {
    const dialogComponentName =
      typeof dialogComponent === 'string' ? dialogComponent : dialogComponent.name;
    if (!this.dialogMapRef.current) {
      this._prepareDialogMapRef();
    }
    const dialogId = this.dialogMapRef.current[dialogComponentName];
    this.openById(dialogId, param, options);;
  }

  close() {
    localStorage.removeItem(DEFERRED_DIALOG_KEY);
    updateSearchParams();
    const html = document.querySelector('html');
    html.style.overflow = 'auto';
    if (Array.isArray(this.history)) {
      this.history.length = 0;
    }
    this.updateState({
      previous: null,
      current: null,
      param: null,
      options: {},
    });
  }

  defer(dialogId) {
    localStorage.setItem(DEFERRED_DIALOG_KEY, dialogId);
  }

  goBack() {
    if (this.history.length === 0) {
      this.close();
      return;
    }
    const lastDialog = this.history.pop();
    this.updateState({
      previous: this.history[this.history.length - 1] || null,
      current: lastDialog,
      param: null,
    });
    updateSearchParams(lastDialog, null);
  }

  openById(dialogId, param, requestedOptions = {}) {
    if (!dialogId) {
      return;
    }
    localStorage.removeItem(DEFERRED_DIALOG_KEY);
    updateSearchParams(dialogId, param);
    const html = document.querySelector('html');
    html.style.overflow = 'hidden';
    const { current } = this;
    if (current) {
      this.history.push(current);
    }
    const predefinedOptions = this.dialogSettings[dialogId]?.options ?? {};
    this.updateState({
      previous: this.history[this.history.length - 1] || null,
      current: dialogId,
      param,
      options: { ...predefinedOptions, ...requestedOptions },
    });
  }

  _prepareDialogMapRef() {
    this.dialogMapRef.current = Object.entries(this.dialogSettings).reduce(
      (container, [dialogId, dialogInfo]) => {
        container[dialogInfo.dialog] = dialogId;
        return container;
      },
      {}
    );
  }
}

export default injectedService(
  {
    name: 'DialogServiceProvider',
    services: ['api'],
    components: ['DialogPortal'],
  },
  function DialogServiceProvider({
    id,
    children,
    dialogRootElement,
    dialogSettings,
    services: { api },
    components: { DialogPortal },
  }) {
    const dialogMapRef = useRef();
    return (
      <ServiceProvider
        serviceId={id}
        serviceClass={DialogService}
        dependencies={{ api, dialogSettings }}
        refs={{ dialogMapRef }}
      >
        {children}
        <DialogPortal dialogRootElement={dialogRootElement} />
      </ServiceProvider>
    );
  }
);
